import {
  CampaignCube,
  CampaignDimension,
  EmailCampaignFunnelCube,
  WelcomeCampaignFunnelCube,
} from "@/cube"
import {cubejsApi} from "@/cube/cubejsApi"

const MEMBER_PLAN_ID_DIMENSION = "Member.planId"

export interface CampaignResult {
  [CampaignCube.campaignType]: string
  [CampaignCube.eventDate]: string
  [CampaignCube.total]: number
}

export interface EmailCampaignResult {
  [EmailCampaignFunnelCube.conversions]: number
  [EmailCampaignFunnelCube.step]: string
  [EmailCampaignFunnelCube.time]: string
}

export interface WelcomeEmailCampaignResult {
  [WelcomeCampaignFunnelCube.conversions]: number
  [WelcomeCampaignFunnelCube.step]: string
  [WelcomeCampaignFunnelCube.time]: string
}

export interface Campaign {
  type: string
  date: string
  recipients: number
  opened: number
  clicked: number
}

export class CampaignService {
  static readonly CAMPAIGN_TYPE_EMAIL = "SuperScore email"
  static readonly CAMPAIGN_TYPE_WELCOME = "Welcome email"
  static readonly CAMPAIGN_TYPE_REPORT = "SuperScore report"
  static readonly CAMPAIGN_STEP_OPEN_EMAIL = "Open Email"
  static readonly CAMPAIGN_STEP_CLICK_EMAIL = "Click Email"

  _campaigns?: Array<Campaign>

  private static async queryCampaigns(planId: string): Promise<Array<CampaignResult>> {
    const query = <const>{
      measures: [CampaignCube.total],
      filters: [
        {
          dimension: MEMBER_PLAN_ID_DIMENSION,
          operator: "equals",
          values: [planId],
        },
      ],
      dimensions: [CampaignCube.campaignType, CampaignCube.eventDate],
      order: {
        [CampaignCube.eventDate]: "desc",
      },
    }
    return <Array<CampaignResult>>(await cubejsApi.load(query)).loadResponse.results[0].data
  }

  private static async queryEmailDetails(planId: string): Promise<Array<EmailCampaignResult>> {
    const query = <const>{
      measures: [EmailCampaignFunnelCube.conversions],
      filters: [
        {
          dimension: MEMBER_PLAN_ID_DIMENSION,
          operator: "equals",
          values: [planId],
        },
      ],
      dimensions: [EmailCampaignFunnelCube.step, EmailCampaignFunnelCube.time],
      order: {
        [EmailCampaignFunnelCube.time]: "desc",
      },
    }
    return <Array<EmailCampaignResult>>(await cubejsApi.load(query)).loadResponse.results[0].data
  }

  private static async queryWelcomeEmailDetails(
    planId: string
  ): Promise<Array<WelcomeEmailCampaignResult>> {
    const query = <const>{
      measures: [WelcomeCampaignFunnelCube.conversions],
      filters: [
        {
          dimension: MEMBER_PLAN_ID_DIMENSION,
          operator: "equals",
          values: [planId],
        },
      ],
      dimensions: [WelcomeCampaignFunnelCube.step, WelcomeCampaignFunnelCube.time],
      order: {
        [WelcomeCampaignFunnelCube.time]: "desc",
      },
    }
    return <Array<WelcomeEmailCampaignResult>>(
      (await cubejsApi.load(query)).loadResponse.results[0].data
    )
  }

  private static extractStepCounts(
    campaign: CampaignResult,
    emailCampaigns: Array<EmailCampaignResult>,
    welcomeEmailCampaigns: Array<WelcomeEmailCampaignResult>
  ): [number, number] {
    if (campaign[CampaignCube.campaignType] === CampaignService.CAMPAIGN_TYPE_EMAIL) {
      return CampaignService.extractEmailStepCounts(campaign, emailCampaigns)
    }

    if (campaign[CampaignCube.campaignType] === CampaignService.CAMPAIGN_TYPE_WELCOME) {
      return CampaignService.extractWelcomeStepCounts(campaign, welcomeEmailCampaigns)
    }

    return [0, 0]
  }

  private static extractEmailStepCounts(
    campaign: CampaignResult,
    emailCampaigns: Array<EmailCampaignResult>
  ): [number, number] {
    let opened = 0
    let clicked = 0
    for (const step of emailCampaigns) {
      if (step[EmailCampaignFunnelCube.time] !== campaign[CampaignCube.eventDate]) continue

      if (step[EmailCampaignFunnelCube.step] === CampaignService.CAMPAIGN_STEP_OPEN_EMAIL) {
        opened = step[EmailCampaignFunnelCube.conversions]
      }

      if (step[EmailCampaignFunnelCube.step] === CampaignService.CAMPAIGN_STEP_CLICK_EMAIL) {
        clicked = step[EmailCampaignFunnelCube.conversions]
      }
    }

    return [opened, clicked]
  }

  private static extractWelcomeStepCounts(
    campaign: CampaignResult,
    welcomeEmailCampaigns: Array<WelcomeEmailCampaignResult>
  ): [number, number] {
    // WelcomeCampaignFunnel.time is the earliest send date of the month
    // (e.g. "2026-08-05"), while Campaign.eventDate is LAST_DAY of the
    // month (e.g. "2026-08-31"), so match by year-month prefix instead.
    const campaignYearMonth = campaign[CampaignCube.eventDate].slice(0, 7)
    let opened = 0
    let clicked = 0
    for (const step of welcomeEmailCampaigns) {
      if (!step[WelcomeCampaignFunnelCube.time].startsWith(campaignYearMonth)) continue

      if (step[WelcomeCampaignFunnelCube.step] === CampaignService.CAMPAIGN_STEP_OPEN_EMAIL) {
        opened = step[WelcomeCampaignFunnelCube.conversions]
      }

      if (step[WelcomeCampaignFunnelCube.step] === CampaignService.CAMPAIGN_STEP_CLICK_EMAIL) {
        clicked = step[WelcomeCampaignFunnelCube.conversions]
      }
    }

    return [opened, clicked]
  }

  async getCampaigns(planId: string): Promise<Array<Campaign>> {
    const [campaignResults, campaignData, welcomeEmailData] = await Promise.all([
      CampaignService.queryCampaigns(planId),
      CampaignService.queryEmailDetails(planId),
      CampaignService.queryWelcomeEmailDetails(planId),
    ])
    this._campaigns = []
    for (const campaign of campaignResults) {
      const [opened, clicked] = CampaignService.extractStepCounts(
        campaign,
        campaignData,
        welcomeEmailData
      )
      if (campaign[CampaignDimension.campaignType] === "Consult") {
        continue
      }
      this._campaigns.push({
        type: campaign[CampaignCube.campaignType],
        date: campaign[CampaignCube.eventDate],
        recipients: campaign[CampaignCube.total],
        opened,
        clicked,
      })
    }

    return this._campaigns
  }

  reset(): void {
    this._campaigns = undefined
  }
}
